import React, { useEffect, useMemo, useState } from 'react';

import type {
  GridApiPremium,
  GridFilterModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { gridFilterModelSelector } from '@mui/x-data-grid-premium';
import { RotateCcw, Trash2 } from 'lucide-react';
import { AlineButton, ConfirmationDialog, getIsReportGridRow } from 'src/components';
import { useDispatchSuccessSnack } from 'src/helpers';
import {
  useDeleteTrashedDocuments,
  useNotificationDispatch,
  useRestoreTrashedDocuments,
} from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

type DialogState = 'closed' | 'confirmRemove' | 'confirmRestore';
type ConfirmAllState = 'closed' | 'confirmRemoveAll' | 'confirmRestoreAll';

interface RepositoryTrashFooterProps {
  apiRef: React.RefObject<GridApiPremium | null>;
  documentCount?: number;
  onRefreshGrid?: () => void;
  onRemoveDocumentsFromGrid?: (documentIds: readonly UUID[]) => void;
  repositoryId?: UUID;
}

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(1.5),
    width: '100%',
  },
}));

export function RepositoryTrashActions({
  apiRef,
  documentCount,
  onRefreshGrid,
  onRemoveDocumentsFromGrid,
  repositoryId,
}: RepositoryTrashFooterProps): JSX.Element | null {
  const { classes } = useStyles();
  const dispatch = useNotificationDispatch();
  const { deleteDocuments } = useDeleteTrashedDocuments();
  const { restoreDocuments } = useRestoreTrashedDocuments();
  const [dialogState, setDialogState] = useState<DialogState>('closed');
  const [confirmAllState, setConfirmAllState] = useState<ConfirmAllState>('closed');
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    ids: new Set(),
    type: 'include',
  });
  const [currentFilterModel, setCurrentFilterModel] = useState<GridFilterModel>({ items: [] });
  const dispatchRestoreSuccessSnack = useDispatchSuccessSnack({
    message: 'Documents successfully restored.',
  });

  useEffect(() => {
    return apiRef.current?.subscribeEvent('rowSelectionChange', setRowSelectionModel);
  }, [apiRef]);

  useEffect(() => {
    setCurrentFilterModel(gridFilterModelSelector(apiRef));
    return apiRef.current?.subscribeEvent('filterModelChange', setCurrentFilterModel);
  }, [apiRef]);

  const selectedDocumentIds = useMemo(() => {
    return rowSelectionModel.ids
      .keys()
      .map(id => String(id))
      .filter(id => !String(id).includes('skeleton'))
      .toArray();
  }, [rowSelectionModel.ids]);
  const totalDocumentCount = documentCount ?? 0;
  const shouldRunForAllDocuments = getShouldRunForAllDocuments({
    apiRef,
    selectedDocumentIds,
    totalDocumentCount,
  });
  const affectedCount = shouldRunForAllDocuments ? totalDocumentCount : selectedDocumentIds.length;
  const hasSelection = selectedDocumentIds.length > 0;
  const isFilterApplied = hasAnyFilter(currentFilterModel);

  if (!repositoryId || !onRemoveDocumentsFromGrid || !onRefreshGrid) return null;

  function handleMutationResult(
    affectedDocumentIds: readonly UUID[],
    skippedDocumentIds: readonly UUID[]
  ) {
    if (!shouldRunForAllDocuments) {
      onRemoveDocumentsFromGrid?.(affectedDocumentIds);
      return;
    }
    const loadedDocIds: UUID[] = [];
    const skippedSet = new Set(skippedDocumentIds);
    for (const row of apiRef.current?.getRowModels().values() ?? []) {
      if (!getIsReportGridRow(row)) continue;
      if (skippedSet.has(row.documentId)) continue;
      loadedDocIds.push(row.documentId);
    }
    if (loadedDocIds.length === 0) {
      onRefreshGrid?.();
      return;
    }
    onRemoveDocumentsFromGrid?.(loadedDocIds);
  }

  function dispatchNotification(message: string) {
    dispatch({
      payload: {
        autoHideDuration: 15000,
        key: uuid4(),
        message,
        variant: 'success',
      },
      type: NotificationsActionType.PUSH_NOTIFICATION,
    });
  }

  async function handleDeleteDocuments() {
    const filterModel = gridFilterModelSelector(apiRef);
    const { data } = await deleteDocuments({
      variables: {
        input: {
          documentIds: shouldRunForAllDocuments ? [] : selectedDocumentIds,
          filterModel,
          reportId: repositoryId!,
          runAllDocs: shouldRunForAllDocuments,
        },
      },
    });
    if (!data) return;
    const { numSkipped } = data.deleteDocuments;
    const removedCount = shouldRunForAllDocuments
      ? totalDocumentCount - numSkipped
      : data.deleteDocuments.documentIds.length;
    const skippedMsg =
      numSkipped > 0
        ? ` Skipped ${numSkipped} ${pluralizeDocuments(numSkipped)} because those files are part of an active Workflow or you are not authorized.`
        : '';
    dispatchNotification(
      `Permanently removed ${removedCount} ${pluralizeDocuments(removedCount)}.${skippedMsg}`
    );
    handleMutationResult(data.deleteDocuments.documentIds, data.deleteDocuments.skippedDocumentIds);
  }

  async function handleRestoreDocuments() {
    const filterModel = gridFilterModelSelector(apiRef);
    const { data } = await restoreDocuments({
      variables: {
        input: {
          documentIds: shouldRunForAllDocuments ? [] : selectedDocumentIds,
          filterModel,
          reportId: repositoryId!,
          runAllDocs: shouldRunForAllDocuments,
        },
      },
    });
    if (!data) return;
    const { numSkipped } = data.restoreDocuments;
    const restoredCount = shouldRunForAllDocuments
      ? totalDocumentCount - numSkipped
      : data.restoreDocuments.documentIds.length;
    if (numSkipped > 0) {
      const skippedMsg = ` Skipped ${numSkipped} ${pluralizeDocuments(numSkipped)}.`;
      dispatchNotification(
        `Restored ${restoredCount} ${pluralizeDocuments(restoredCount)}.${skippedMsg}`
      );
    } else {
      dispatchRestoreSuccessSnack();
    }
    handleMutationResult(
      data.restoreDocuments.documentIds,
      data.restoreDocuments.skippedDocumentIds
    );
  }

  const closeDialog = () => setDialogState('closed');
  const closeConfirmAll = () => setConfirmAllState('closed');

  return (
    <>
      <div className={classes.actions}>
        <AlineButton
          color="primary"
          disabled={!hasSelection}
          startIcon={<RotateCcw size={16} />}
          variant="outlined"
          onClick={() =>
            shouldRunForAllDocuments
              ? setConfirmAllState('confirmRestoreAll')
              : setDialogState('confirmRestore')
          }
        >
          Restore
        </AlineButton>
        <AlineButton
          color="primary"
          disabled={!hasSelection}
          startIcon={<Trash2 size={16} />}
          variant="contained"
          onClick={() =>
            shouldRunForAllDocuments
              ? setConfirmAllState('confirmRemoveAll')
              : setDialogState('confirmRemove')
          }
        >
          Delete
        </AlineButton>
      </div>
      <ConfirmationDialog
        message={`Are you sure you want to permanently delete ${affectedCount} ${pluralizeDocuments(affectedCount)}?`}
        open={dialogState === 'confirmRemove'}
        primaryText="Delete"
        title="Delete documents"
        onClickPrimary={handleDeleteDocuments}
        onClose={closeDialog}
      />
      <ConfirmationDialog
        message={
          isFilterApplied
            ? 'This will permanently delete ALL trashed documents matching the current filter. This action cannot be undone. Are you sure?'
            : `This will permanently delete ALL ${totalDocumentCount} trashed documents. This action cannot be undone. Are you sure?`
        }
        open={confirmAllState === 'confirmRemoveAll'}
        primaryText="Yes, delete all"
        secondaryText="No"
        title="Delete all trashed documents?"
        onClickPrimary={handleDeleteDocuments}
        onClose={closeConfirmAll}
      />
      <ConfirmationDialog
        message={`Are you sure you want to restore ${affectedCount} ${pluralizeDocuments(affectedCount)}?`}
        open={dialogState === 'confirmRestore'}
        primaryText="Restore"
        title="Restore documents"
        onClickPrimary={handleRestoreDocuments}
        onClose={closeDialog}
      />
      <ConfirmationDialog
        message={
          isFilterApplied
            ? 'This will restore ALL trashed documents matching the current filter. Are you sure?'
            : `This will restore ALL ${totalDocumentCount} trashed documents. Are you sure?`
        }
        open={confirmAllState === 'confirmRestoreAll'}
        primaryText="Yes, restore all"
        secondaryText="No"
        title="Restore all trashed documents?"
        onClickPrimary={handleRestoreDocuments}
        onClose={closeConfirmAll}
      />
    </>
  );
}

function pluralizeDocuments(count: number) {
  return `document${count === 1 ? '' : 's'}`;
}

function hasAnyFilter(filterModel: GridFilterModel): boolean {
  return filterModel.items.length > 0 || (filterModel.quickFilterValues?.length ?? 0) > 0;
}

function getShouldRunForAllDocuments({
  apiRef,
  selectedDocumentIds,
  totalDocumentCount,
}: {
  apiRef: React.RefObject<GridApiPremium | null>;
  selectedDocumentIds: UUID[];
  totalDocumentCount: number;
}) {
  const rowIds = apiRef.current?.getAllRowIds() ?? [];
  const nonSkeletonRowIds = rowIds.map(id => String(id)).filter(id => !id.includes('skeleton'));
  if (!nonSkeletonRowIds.length || totalDocumentCount <= 0) return false;

  const selectedIdSet = new Set(selectedDocumentIds);
  return nonSkeletonRowIds.every(id => selectedIdSet.has(id));
}
